import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Send, Share2, Pencil, Copy, Trash2, Upload, RefreshCw, Save, Download, Plus, Image as ImageIcon, ChevronLeft, ChevronRight, HardDrive,
  Instagram, Facebook, Linkedin, Music2, Pin, FileText, MessageSquare, History, ListTodo,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api, { apiError, uploadFile } from "@/lib/api";
import MediaPicker from "@/components/MediaPicker";
import { PLATFORMS, FORMATS, OBJECTIVES, STATUSES, isVideo } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { PublishPanel } from "@/components/PublishPanel";
import { NotifyClientButton } from "@/components/NotifyClientButton";
import CarouselViewer from "@/components/CarouselViewer";
import VisualEditor from "@/components/VisualEditor";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const AI_ACTIONS = [
  { id: "generer", label: "Générer" },
  { id: "raccourcir", label: "Raccourcir" },
  { id: "allonger", label: "Allonger" },
  { id: "plus_professionnel", label: "Plus professionnel" },
  { id: "plus_naturel", label: "Plus naturel" },
  { id: "plus_premium", label: "Plus premium" },
  { id: "plus_humoristique", label: "Plus humoristique" },
  { id: "ajouter_emojis", label: "Ajouter des emojis" },
  { id: "retirer_emojis", label: "Retirer les emojis" },
  { id: "corriger", label: "Corriger l'orthographe" },
  { id: "reformuler", label: "Reformuler" },
];

/* Réseaux sélectionnables (multi) — Meta = publication auto, autres = planification */
const NETWORKS = [
  { id: "instagram", label: "Instagram", Icon: Instagram, color: "#E1306C" },
  { id: "facebook", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "#0A66C2" },
  { id: "tiktok", label: "TikTok", Icon: Music2, color: "#0F172A" },
  { id: "pinterest", label: "Pinterest", Icon: Pin, color: "#E60023" },
];

function AiField({ label, field, value, onChange, onAi, loading, rows = 3, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-xs">{label}</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`ai-${field}-btn`} disabled={loading}
              className="flex items-center gap-1 text-[11px] font-medium text-neutral-900 bg-neutral-100 hover:bg-neutral-200 rounded-full px-2.5 py-1 transition-colors disabled:opacity-50">
              <Sparkles className="w-3 h-3" /> {loading ? "Génération…" : "Générer avec l'IA"}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
            {AI_ACTIONS.map((a) => (
              <DropdownMenuItem key={a.id} data-testid={`ai-${field}-${a.id}`} onClick={() => onAi(field, a.id)}>{a.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(field, e.target.value)} className="text-[13px]" />
    </div>
  );
}

export default function PublicationDetail() {
  const { pubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else if (pub) navigate(`/clients/${pub.client_id}`);
    else navigate("/contenus");
  };
  const [pub, setPub] = useState(null);
  const [form, setForm] = useState({});
  const [aiLoading, setAiLoading] = useState("");
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tab, setTab] = useState("contenu");
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveLink, setDriveLink] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/publications/${pubId}`, {
        title: form.title, description: form.description, hashtags: form.hashtags,
        cta: form.cta, platform: form.platform, format: form.format,
        objective: form.objective, date: form.date, time: form.time, status: form.status,
        channels: form.channels,
      });
      toast.success("Publication enregistrée");
      goBack();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const load = useCallback(() => {
    api.get(`/publications/${pubId}`).then((r) => {
      setPub(r.data);
      setForm({
        title: r.data.title, description: r.data.description, hashtags: r.data.hashtags,
        cta: r.data.cta, platform: r.data.platform, format: r.data.format,
        objective: r.data.objective, date: r.data.date, time: r.data.time, status: r.data.status,
        channels: r.data.channels && Object.values(r.data.channels).some(Boolean)
          ? r.data.channels
          : { instagram: r.data.platform !== "facebook", facebook: r.data.platform === "facebook" },
      });
    }).catch(() => navigate("/"));
  }, [pubId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!pub) return <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;

  const patch = async (update) => {
    try {
      const { data } = await api.patch(`/publications/${pubId}`, update);
      setPub((p) => ({ ...p, ...data }));
      window.dispatchEvent(new Event("sf:pubs")); // maj auto feed, calendrier, miniatures
      return data;
    } catch { toast.error("Erreur d'enregistrement"); }
  };
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (!["title", "description", "hashtags", "cta"].includes(k)) patch({ [k]: v });
  };
  const blurSave = (k) => { if (form[k] !== pub[k]) patch({ [k]: form[k] }); };

  /* Multi-réseaux : au moins un canal actif ; platform legacy = premier réseau actif (cohérence filtres) */
  const toggleNetwork = (id) => {
    const next = { ...(form.channels || {}), [id]: !form.channels?.[id] };
    if (!Object.values(next).some(Boolean)) { toast.error("Sélectionnez au moins un réseau"); return; }
    const platform = Object.keys(next).find((k) => next[k]) || "instagram";
    setForm((f) => ({ ...f, channels: next, platform }));
    patch({ channels: next, platform });
  };

  const runAi = async (field, action) => {
    setAiLoading(field);
    try {
      const { data } = await api.post(`/ai/publications/${pubId}/text`, {
        field, action, current_text: form[field],
      });
      setForm((f) => ({ ...f, [field]: data.text }));
      await patch({ [field]: data.text });
      toast.success("Texte généré avec l'identité de marque");
    } catch { toast.error("La génération a échoué"); }
    finally { setAiLoading(""); }
  };

  const visual = form.visual_url || pub.visual_url;
  const addTask = async () => {
    try {
      await api.post("/tasks", { title: `Modifier publication — ${pub.title}`, client_id: pub.client_id,
        service: "Community Management", deadline: pub.date,
        source_type: "publication", source_id: pub.id,
        source_label: `Publication — ${pub.title}`, source_url: `/publications/${pub.id}` });
      toast.success("Tâche ajoutée à la To-do");
      window.dispatchEvent(new Event("sf:tasks"));
    } catch { toast.error("Création impossible"); }
  };
  const isCarousel = form.format === "carrousel";
  const slides = (pub.slides && pub.slides.length) ? pub.slides : (visual ? [visual] : []);
  // Ratio d'aperçu selon le format : Post/Carrousel 1080×1350 (4:5), Reel/Story 1080×1920 (9:16)
  const isVertical = ["reel", "story"].includes(pub.format);
  const ratioCls = isVertical ? "aspect-[9/16]" : "aspect-[4/5]";
  const dimLabel = isVertical ? "1080 × 1920 px" : "1080 × 1350 px";

  const pickFromLibrary = async (m) => {
    const url = m.url;
    if (isCarousel) {
      await patch({ slides: [...slides, url] });
      toast.success("Visuel ajouté au carrousel depuis la médiathèque");
    } else {
      await patch({ visual_url: url });
      toast.success("Visuel ajouté depuis la médiathèque");
    }
  };

  const uploadVisual = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      await patch({ visual_url: data.url });
      toast.success("Visuel mis à jour");
    } catch { toast.error("Échec du téléversement"); }
    e.target.value = "";
  };

  const uploadToDrive = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setDriveBusy(true);
    try {
      const data = await uploadFile(file, { client_id: pub.client_id, dest: "drive" });
      await patch({ visual_url: data.url });
      toast.success("Vidéo stockée sur Google Drive — l'aperçu reste visible ici");
      setDriveOpen(false);
    } catch (err) { toast.error(apiError(err) || "Échec du téléversement vers Drive"); }
    finally { setDriveBusy(false); }
  };

  const attachDriveLink = async () => {
    if (!driveLink.trim()) return;
    setDriveBusy(true);
    try {
      const { data } = await api.post("/upload/drive-link", { url: driveLink.trim() });
      await patch({ visual_url: data.url });
      toast.success(`Fichier Drive joint : ${data.name}`);
      setDriveOpen(false);
      setDriveLink("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setDriveBusy(false); }
  };

  const share = async () => {
    try {
      await patch({ status: "a_valider", validation_status: "envoye" });
      setForm((f) => ({ ...f, status: "a_valider" }));
      const { data } = await api.post(`/clients/${pub.client_id}/review-link?notify=true`);
      const url = `${window.location.origin}/review/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(
        data.email_sent
          ? "Lien client copié — email de validation envoyé au client"
          : "Lien client copié — publication envoyée en validation",
        { description: url });
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Ce client n'existe plus ou a été archivé. Revenez à la liste des clients.");
        window.dispatchEvent(new Event("sf:clients"));
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/publications/${pubId}/comments`, { text: comment, internal });
    setComment(""); setInternal(false);
    load();
  };

  const resend = async () => {
    try {
      const { data } = await api.post(`/publications/${pubId}/resend`);
      setPub((p) => ({ ...p, ...data }));
      setForm((f) => ({ ...f, status: "a_valider" }));
      toast.success("Publication renvoyée au client", { description: "Le client verra la mention « Nouvelle version disponible » sur son espace." });
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = async () => {
    const { data } = await api.post(`/publications/${pubId}/duplicate`);
    toast.success("Publication dupliquée");
    navigate(`/publications/${data.id}`);
  };

  const remove = async () => {
    await api.delete(`/publications/${pubId}`);
    toast.success("Publication archivée");
    goBack();
  };

  const addSlides = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const urls = [...(pub.slides || [])];
    for (const file of files) {
      try {
        const data = await uploadFile(file);
        urls.push(data.url);
      } catch { toast.error(`Échec : ${file.name}`); }
    }
    await patch({ slides: urls });
    toast.success(`${files.length} slide(s) ajoutée(s)`);
    e.target.value = "";
  };

  const removeSlide = async (i) => {
    await patch({ slides: (pub.slides || []).filter((_, j) => j !== i) });
    toast.success("Slide supprimée");
  };

  const reorderSlides = async (from, to) => {
    const arr = [...(pub.slides || [])];
    if (to < 0 || to >= arr.length || from === to) return;
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    await patch({ slides: arr });
    toast.success("Ordre des slides mis à jour");
  };

  const onThumbDrop = async (e, to) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData("text/slide-index"), 10);
    if (!Number.isNaN(from)) await reorderSlides(from, to);
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      await patch({ cover_url: data.url });
      toast.success("Couverture du Reel enregistrée — elle apparaîtra dans le feed");
    } catch { toast.error("Échec du téléversement"); }
    e.target.value = "";
  };

  const TABS = [
    { id: "contenu", label: "Contenu", Icon: FileText },
    { id: "commentaires", label: "Commentaires", Icon: MessageSquare, count: (pub.comments || []).length },
    { id: "historique", label: "Historique", Icon: History, count: (pub.activity || []).length },
  ];

  return (
    <div className="space-y-4" data-testid="publication-detail">
      {/* En-tête */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <button onClick={goBack} className="p-2 rounded-lg hover:bg-white text-neutral-500 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-neutral-400 uppercase tracking-wide">
            <Link to={`/clients/${pub.client_id}`} className="hover:underline" style={{ color: pub.client?.color }}>{pub.client?.name}</Link>
          </p>
          <h1 className="font-heading text-xl sm:text-2xl font-bold truncate text-[#0F172A]" data-testid="pub-title">{pub.title}</h1>
        </div>
        <StatusBadge status={form.status} />
        {form.status === "modification" && (
          <Button size="sm" data-testid="resend-to-client-btn" onClick={resend}
            className="gap-1.5 bg-[#0369A1] hover:bg-[#075985] text-white rounded-xl">
            <RefreshCw className="w-3.5 h-3.5" /> Renvoyer au client
          </Button>
        )}
        {form.status === "a_valider" && <NotifyClientButton clientId={pub.client_id} />}
        <Button variant="outline" size="sm" data-testid="duplicate-btn" onClick={duplicate} className="gap-1 rounded-xl"><Copy className="w-3.5 h-3.5" /> Dupliquer</Button>
        <Button variant="outline" size="sm" data-testid="pub-add-task" onClick={addTask} className="gap-1 rounded-xl"><ListTodo className="w-3.5 h-3.5" /> Tâche</Button>
        <Button variant="outline" size="sm" data-testid="delete-btn" onClick={remove} className="gap-1 text-red-600 hover:text-red-700 rounded-xl"><Trash2 className="w-3.5 h-3.5" /></Button>
        <Button size="sm" data-testid="share-btn" onClick={share} className="gap-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl">
          <Share2 className="w-3.5 h-3.5" /> Partager avec le client
        </Button>
        <Button size="sm" data-testid="save-btn" onClick={saveAll} disabled={saving}
          className="gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-xl">
          <Save className="w-3.5 h-3.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* Visuel + panneau Meta */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
            <div className={`${isCarousel ? (slides.length ? `${ratioCls} bg-neutral-50` : `${ratioCls} bg-neutral-100`) : !visual ? `${ratioCls} bg-neutral-100` : isVideo(visual) ? "aspect-[9/16] bg-black" : `${ratioCls} bg-neutral-50`} ${isVertical && !isVideo(visual) ? "h-[480px] mx-auto" : "w-full"} flex items-center justify-center relative overflow-hidden`} data-testid="pub-visual">
            <span className="absolute bottom-2 left-2 z-10 bg-black/55 text-white text-[9px] font-medium rounded-full px-2 py-0.5" data-testid="pub-visual-ratio">{dimLabel}</span>
              {isCarousel ? (
                slides.length > 0 ? (
                  <CarouselViewer slides={slides} title={pub.title} testid="pub-carousel" />
                ) : (
                  <div className="text-center p-8">
                    <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: pub.client?.color }}>
                      <Pencil className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-sm text-neutral-500">Ajoutez les slides du carrousel ci-dessous</p>
                  </div>
                )
              ) : visual ? (
                isVideo(visual) ? (
                  <video src={visual} controls playsInline className="w-full h-full object-contain" data-testid="pub-visual-video" />
                ) : (
                  <img src={visual} alt={pub.title} className="w-full h-full object-cover" data-testid="pub-visual-img" />
                )
              ) : (
                <div className="text-center p-8">
                  <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: pub.client?.color }}>
                    <Pencil className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm text-neutral-500">Aucun visuel pour le moment</p>
                </div>
              )}
            </div>
            {isCarousel && slides.length > 0 && (
              <div className="flex gap-2 px-4 pt-3 overflow-x-auto" data-testid="carousel-thumbs">
                <p className="sr-only">Glissez les miniatures ou utilisez les flèches pour réorganiser les slides</p>
                {slides.map((s, i) => (
                  <div key={i} className="relative group shrink-0" data-testid={`slide-thumb-${i}`}
                    draggable={(pub.slides || []).length > 1}
                    onDragStart={(e) => { e.dataTransfer.setData("text/slide-index", String(i)); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onThumbDrop(e, i)}
                  >
                    <img src={s} className="w-14 h-14 object-cover rounded-md border border-neutral-200 cursor-grab active:cursor-grabbing" alt={`Slide ${i + 1}`} draggable={false} />
                    <span className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[9px] rounded px-1 pointer-events-none">{i + 1}</span>
                    {(pub.slides || []).length > 1 && (
                      <>
                        <button onClick={() => reorderSlides(i, i - 1)} disabled={i === 0} data-testid={`slide-move-left-${i}`} title="Déplacer vers la gauche"
                          className="absolute top-1/2 -translate-y-1/2 -left-1.5 w-4 h-4 bg-neutral-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0">
                          <ChevronLeft className="w-3 h-3" />
                        </button>
                        <button onClick={() => reorderSlides(i, i + 1)} disabled={i === slides.length - 1} data-testid={`slide-move-right-${i}`} title="Déplacer vers la droite"
                          className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-4 h-4 bg-neutral-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0">
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    {(pub.slides || []).length > 0 && (
                      <button onClick={() => removeSlide(i)} data-testid={`slide-delete-${i}`}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="p-3 flex flex-wrap gap-2">
              {isCarousel ? (
                <label className="flex-1">
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={addSlides} data-testid="upload-slides-input" />
                  <span className="flex items-center justify-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50 transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Ajouter des slides
                  </span>
                </label>
              ) : (
                <label className="flex-1">
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={uploadVisual} data-testid="upload-visual-input" />
                  <span className="flex items-center justify-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50 transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Téléverser (image ou vidéo)
                  </span>
                </label>
              )}
              <button data-testid="pick-from-library-btn" onClick={() => setPickerOpen(true)}
                className="flex-1 flex items-center justify-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3 py-2 text-xs hover:bg-neutral-50 transition-colors">
                <ImageIcon className="w-3.5 h-3.5" /> Médiathèque
              </button>
              {!isCarousel && (
                <button data-testid="drive-attach-btn" onClick={() => setDriveOpen((o) => !o)}
                  title="Grosses vidéos (> 30 Mo) : stockage direct sur Google Drive"
                  className={`flex-1 flex items-center justify-center gap-1.5 border rounded-xl px-3 py-2 text-xs transition-colors ${driveOpen ? "border-[#0B132B] bg-[#0B132B]/5 font-semibold" : "border-[#E2E8F0] hover:bg-neutral-50"}`}>
                  <HardDrive className="w-3.5 h-3.5" /> Drive
                </button>
              )}
              <Button data-testid="open-editor-btn" onClick={() => setEditorOpen(true)} className="flex-1 bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5 rounded-xl text-xs">
                <Pencil className="w-3.5 h-3.5" /> Éditer le visuel
              </Button>
              {visual && (
                <a href={visual} download target="_blank" rel="noreferrer" data-testid="download-visual-btn"
                  className="flex items-center justify-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3 py-2 text-xs hover:bg-neutral-50 transition-colors"
                  title="Télécharger le fichier">
                  <Download className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {visual?.includes("/socialflow/drive/") && (
              <p className="px-3 pb-2 text-[10px] text-neutral-400 flex items-center gap-1" data-testid="drive-stored-hint">
                <HardDrive className="w-3 h-3" /> Vidéo stockée sur Google Drive — aperçu servi à la demande
              </p>
            )}
            {driveOpen && (
              <div className="border-t border-neutral-100 p-3 space-y-2" data-testid="drive-panel">
                <p className="text-[11px] text-neutral-500">Grosse vidéo (&gt; 30 Mo) ? Stockez-la sur Google Drive : elle ne pèse plus sur l'app, l'aperçu reste visible ici et pour le client.</p>
                <label className={`flex items-center justify-center gap-1.5 border border-dashed border-neutral-300 rounded-xl px-3 py-2.5 text-xs transition-colors ${driveBusy ? "opacity-50" : "cursor-pointer hover:bg-neutral-50"}`}>
                  <input type="file" accept="video/*" className="hidden" onChange={uploadToDrive} disabled={driveBusy} data-testid="drive-upload-input" />
                  <Upload className="w-3.5 h-3.5" /> {driveBusy ? "Envoi vers Google Drive…" : "Téléverser une vidéo vers Drive"}
                </label>
                <div className="flex gap-1.5">
                  <Input value={driveLink} onChange={(e) => setDriveLink(e.target.value)} placeholder="ou collez un lien Google Drive…" className="text-xs h-9" data-testid="drive-link-input" />
                  <Button onClick={attachDriveLink} disabled={driveBusy || !driveLink.trim()} data-testid="drive-link-submit"
                    className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl text-xs h-9">Joindre</Button>
                </div>
              </div>
            )}
            {visual && isVideo(visual) && (
              <div className="border-t border-neutral-100 p-4" data-testid="reel-cover-section">
                <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2">Couverture du Reel (1080×1350)</p>
                <div className="flex items-center gap-3">
                  {pub.cover_url ? (
                    <img src={pub.cover_url} alt="Couverture du Reel" className="w-16 rounded-md border border-neutral-200 object-cover" style={{ aspectRatio: "4/5" }} data-testid="reel-cover-preview" />
                  ) : (
                    <div className="w-16 rounded-md bg-neutral-100 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-300" style={{ aspectRatio: "4/5" }}>
                      <ImageIcon className="w-5 h-5" />
                    </div>
                  )}
                  <label>
                    <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="upload-cover-input" />
                    <span className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-1.5 cursor-pointer hover:bg-neutral-50 transition-colors">
                      {pub.cover_url ? "Remplacer" : "Choisir une image"}
                    </span>
                  </label>
                  {pub.cover_url && (
                    <button onClick={() => patch({ cover_url: "" })} data-testid="reel-cover-delete"
                      className="text-neutral-300 hover:text-red-500 transition-colors" title="Retirer la couverture">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-neutral-400 mt-2">Elle apparaîtra dans le feed Instagram, rognée aux dimensions de la grille.</p>
              </div>
            )}
          </div>

          <PublishPanel pub={pub} onChanged={load} />

          <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} clientId={pub.client_id} onSelect={pickFromLibrary} />
        </div>

        {/* Contenu / Commentaires / Historique — onglets */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
            <div className="flex gap-1 px-4 pt-3 border-b border-neutral-100" data-testid="pub-tabs">
              {TABS.map((t) => (
                <button key={t.id} data-testid={`pub-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 rounded-t-lg border-b-2 transition-colors ${
                    tab === t.id ? "border-[#0B132B] text-[#0F172A]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
                  <t.Icon className="w-3.5 h-3.5" /> {t.label}
                  {t.count > 0 && <span className="text-[9px] bg-neutral-100 text-neutral-500 rounded-full px-1.5">{t.count}</span>}
                </button>
              ))}
            </div>

            {tab === "contenu" && (
              <div className="p-5 space-y-4" data-testid="pub-tab-panel-contenu">
                <div>
                  <Label className="text-xs">Titre</Label>
                  <Input data-testid="pub-title-input" className="mt-1.5 font-medium text-[13px]" value={form.title || ""}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} onBlur={() => blurSave("title")} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="col-span-2 md:col-span-4">
                    <Label className="text-xs">Plateformes</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="pub-platform">
                      {NETWORKS.map((n) => {
                        const active = !!form.channels?.[n.id];
                        return (
                          <button key={n.id} type="button" data-testid={`pub-platform-${n.id}`} onClick={() => toggleNetwork(n.id)}
                            className={`flex items-center gap-1.5 text-[11px] font-medium rounded-full px-3 py-1.5 border transition-colors ${
                              active ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-neutral-500 border-[#E2E8F0] hover:border-neutral-300"}`}>
                            <n.Icon className="w-3.5 h-3.5" style={active ? {} : { color: n.color }} />
                            {n.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-neutral-400 mt-1">Instagram + Facebook : publication automatique via Meta (si comptes connectés).</p>
                  </div>
                  <div>
                    <Label className="text-xs">Format</Label>
                    <Select value={form.format} onValueChange={(v) => set("format", v)}>
                      <SelectTrigger data-testid="pub-format" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{FORMATS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input data-testid="pub-date" type="date" className="mt-1.5" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Heure</Label>
                    <Input data-testid="pub-time" type="time" className="mt-1.5" value={form.time || ""} onChange={(e) => set("time", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Objectif</Label>
                    <Select value={form.objective} onValueChange={(v) => set("objective", v)}>
                      <SelectTrigger data-testid="pub-objective" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{OBJECTIVES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Statut</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="pub-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <AiField label="Description" field="description" testid="pub-description" value={form.description || ""}
                  onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))} onAi={runAi} loading={aiLoading === "description"} />
                <AiField label="Hashtags" field="hashtags" testid="pub-hashtags" rows={2} value={form.hashtags || ""}
                  onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))} onAi={runAi} loading={aiLoading === "hashtags"} />
                <Button data-testid="save-text-btn" onClick={saveAll} disabled={saving}
                  className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5 rounded-xl">
                  <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </div>
            )}

            {tab === "commentaires" && (
              <div className="p-5" data-testid="pub-tab-panel-commentaires">
                <div className="space-y-4 mb-5 max-h-[420px] overflow-y-auto">
                  {(pub.comments || []).map((c) => (
                    <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                        c.author_type === "client" ? "text-white" : "bg-[#0B132B] text-white"}`}
                        style={c.author_type === "client" ? { background: pub.client?.color } : {}}>
                        {c.author_name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{c.author_name}</span>
                          {c.author_type === "client" && <span className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-1.5">Client</span>}
                          {c.internal && <span className="text-[10px] bg-neutral-100 text-neutral-500 border border-neutral-200 rounded-full px-1.5">Note interne</span>}
                          <span className="text-xs text-neutral-400">{format(parseISO(c.created_at), "d MMM HH:mm", { locale: fr })}</span>
                        </div>
                        <p className="text-sm text-neutral-600 mt-0.5">{c.text}</p>
                      </div>
                    </div>
                  ))}
                  {(pub.comments || []).length === 0 && <p className="text-sm text-neutral-400">Aucun commentaire.</p>}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Écrire un commentaire…" />
                  <Button data-testid="comment-send" onClick={postComment} size="icon" className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white shrink-0 rounded-xl">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <label className="flex items-center gap-2 mt-2 text-xs text-neutral-500 cursor-pointer">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="comment-internal" />
                  Note interne (invisible pour le client)
                </label>
              </div>
            )}

            {tab === "historique" && (
              <div className="p-5" data-testid="pub-tab-panel-historique">
                <div className="space-y-3 max-h-[420px] overflow-y-auto">
                  {(pub.activity || []).map((a) => (
                    <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.id}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 mt-2 shrink-0" />
                      <div>
                        <p><span className="font-medium">{a.actor}</span> {a.action}</p>
                        <p className="text-xs text-neutral-400">{format(parseISO(a.created_at), "d MMM 'à' HH:mm", { locale: fr })}</p>
                      </div>
                    </div>
                  ))}
                  {(pub.activity || []).length === 0 && <p className="text-sm text-neutral-400">Aucun historique.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <VisualEditor open={editorOpen} setOpen={setEditorOpen} publication={pub} onSaved={(url) => setPub((p) => ({ ...p, visual_url: url }))} />
    </div>
  );
}
